/**
 * lua_lint.ts — fast, PURE Lua diagnostics for the IDE (no CodeMirror imports,
 * so it runs under Node in verify:ide). Not a full parser — a token-level
 * checker that catches the mistakes that actually reach the deploy button:
 *
 *   · unbalanced block keywords (function/if/for/while/do … end)
 *   · unbalanced (), {}, [] and unterminated strings
 *   · calls to world./state./obj./contract. names the dialect doesn't expose
 *     (the API catalog is the source of truth)
 *   · contract dialect: obj.get/set first arg must be a numeric literal
 *     (the compiler's hard rule — better to say it here than at deploy)
 *   · world scripts: forgetting to `return { ... }` hooks
 *
 * The server compiler stays the authority — its "line N: msg" errors are
 * mapped onto the same Diagnostic shape by the IDE.
 */
import { knownCalls, type LuaDialect } from './lua_api.js';

export interface LuaDiagnostic {
  line: number;      // 1-based
  col: number;       // 1-based
  message: string;
  severity: 'error' | 'warning';
}

interface Tok { kind: 'kw' | 'name' | 'op' | 'num' | 'str'; text: string; line: number; col: number }

const BLOCK_OPENERS = new Set(['function', 'if', 'for', 'while', 'do', 'repeat']);
const KW = new Set(['and', 'break', 'do', 'else', 'elseif', 'end', 'false', 'for', 'function', 'goto', 'if', 'in',
  'local', 'nil', 'not', 'or', 'repeat', 'return', 'then', 'true', 'until', 'while']);

/** Tokenize enough of Lua for structural checks (comments/strings consumed correctly). */
export function tokenizeLua(src: string, diags?: LuaDiagnostic[]): Tok[] {
  const toks: Tok[] = [];
  let i = 0, line = 1, col = 1;
  const n = src.length;
  const adv = (k: number): void => {
    for (let j = 0; j < k; j++) {
      if (src[i] === '\n') { line++; col = 1; } else col++;
      i++;
    }
  };
  while (i < n) {
    const c = src[i];
    // whitespace
    if (c === ' ' || c === '\t' || c === '\r' || c === '\n') { adv(1); continue; }
    // comments: -- line, --[[ block ]]
    if (c === '-' && src[i + 1] === '-') {
      if (src[i + 2] === '[' && src[i + 3] === '[') {
        const end = src.indexOf(']]', i + 4);
        if (end === -1) { adv(n - i); continue; }
        adv(end + 2 - i);
        continue;
      }
      const nl = src.indexOf('\n', i);
      adv((nl === -1 ? n : nl) - i);
      continue;
    }
    // long string [[ ]]
    if (c === '[' && src[i + 1] === '[') {
      const startLine = line, startCol = col;
      const end = src.indexOf(']]', i + 2);
      if (end === -1) {
        diags?.push({ line: startLine, col: startCol, message: 'unterminated long string [[ … ]]', severity: 'error' });
        adv(n - i);
        continue;
      }
      toks.push({ kind: 'str', text: src.slice(i, end + 2), line: startLine, col: startCol });
      adv(end + 2 - i);
      continue;
    }
    // quoted strings
    if (c === '"' || c === "'") {
      const startLine = line, startCol = col;
      let j = i + 1;
      while (j < n && src[j] !== c) {
        if (src[j] === '\\') j++;
        if (src[j] === '\n') break; // Lua short strings can't span lines
        j++;
      }
      if (j >= n || src[j] !== c) {
        diags?.push({ line: startLine, col: startCol, message: 'unterminated string', severity: 'error' });
        adv(j - i);
        continue;
      }
      toks.push({ kind: 'str', text: src.slice(i, j + 1), line: startLine, col: startCol });
      adv(j + 1 - i);
      continue;
    }
    // numbers
    if (c >= '0' && c <= '9') {
      let j = i;
      while (j < n && /[0-9a-fA-FxX.eE+-]/.test(src[j])) {
        if ((src[j] === '+' || src[j] === '-') && !/[eE]/.test(src[j - 1])) break;
        j++;
      }
      toks.push({ kind: 'num', text: src.slice(i, j), line, col });
      adv(j - i);
      continue;
    }
    // names / keywords (dotted paths captured whole: world.spawn)
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < n && /[A-Za-z0-9_.]/.test(src[j])) j++;
      // trailing dot is an operator situation (e.g. `a. b`) — trim it
      let text = src.slice(i, j);
      while (text.endsWith('.')) { text = text.slice(0, -1); j--; }
      toks.push({ kind: KW.has(text) ? 'kw' : 'name', text, line, col });
      adv(j - i);
      continue;
    }
    // single-char operator/punct
    toks.push({ kind: 'op', text: c, line, col });
    adv(1);
  }
  return toks;
}

/** Structural + API lint. `dialect` picks the known-call set. */
export function lintLua(src: string, dialect: LuaDialect): LuaDiagnostic[] {
  const diags: LuaDiagnostic[] = [];
  const toks = tokenizeLua(src, diags);
  const known = knownCalls(dialect);

  // ── block balance ──────────────────────────────────────────────────────────
  const blocks: Tok[] = [];
  for (let k = 0; k < toks.length; k++) {
    const t = toks[k];
    if (t.kind !== 'kw') continue;
    if (BLOCK_OPENERS.has(t.text)) {
      // `for … do` / `while … do`: the `do` belongs to the same block — skip it
      // by only counting `do` when it does NOT close a for/while header.
      if (t.text === 'do') {
        const prevHeader = blocks[blocks.length - 1];
        if (prevHeader && (prevHeader.text === 'for' || prevHeader.text === 'while') && !(prevHeader as Tok & { closedHeader?: boolean }).closedHeader) {
          (prevHeader as Tok & { closedHeader?: boolean }).closedHeader = true;
          continue;
        }
      }
      blocks.push({ ...t });
    } else if (t.text === 'end') {
      const open = blocks.pop();
      if (!open) diags.push({ line: t.line, col: t.col, message: "'end' with no open block", severity: 'error' });
      else if (open.text === 'repeat') diags.push({ line: t.line, col: t.col, message: "'repeat' closes with 'until', not 'end'", severity: 'error' });
    } else if (t.text === 'until') {
      const open = blocks.pop();
      if (!open || open.text !== 'repeat') diags.push({ line: t.line, col: t.col, message: "'until' with no open 'repeat'", severity: 'error' });
    }
  }
  for (const open of blocks) {
    diags.push({ line: open.line, col: open.col, message: `'${open.text}' is never closed (missing 'end'${open.text === 'repeat' ? " / 'until'" : ''})`, severity: 'error' });
  }

  // ── bracket balance ────────────────────────────────────────────────────────
  const pairs: Record<string, string> = { ')': '(', '}': '{', ']': '[' };
  const stack: Tok[] = [];
  for (const t of toks) {
    if (t.kind !== 'op') continue;
    if (t.text === '(' || t.text === '{' || t.text === '[') stack.push(t);
    else if (t.text === ')' || t.text === '}' || t.text === ']') {
      const open = stack.pop();
      if (!open || open.text !== pairs[t.text]) {
        diags.push({ line: t.line, col: t.col, message: `unbalanced '${t.text}'`, severity: 'error' });
      }
    }
  }
  for (const open of stack) diags.push({ line: open.line, col: open.col, message: `'${open.text}' is never closed`, severity: 'error' });

  // ── unknown API calls + contract objId rule ────────────────────────────────
  for (let k = 0; k < toks.length; k++) {
    const t = toks[k];
    if (t.kind !== 'name' || !t.text.includes('.')) continue;
    const ns = t.text.split('.')[0];
    if (!['world', 'state', 'obj', 'contract'].includes(ns)) continue;
    const isCall = toks[k + 1]?.kind === 'op' && toks[k + 1].text === '(';
    if (!isCall) continue;
    if (!known.has(t.text)) {
      const other = dialect === 'worldscript' ? 'contract' : 'worldscript';
      const inOther = knownCalls(other).has(t.text);
      diags.push({
        line: t.line, col: t.col, severity: 'warning',
        message: inOther
          ? `${t.text} belongs to the ${other === 'contract' ? 'server contract' : 'world script'} dialect — not available here`
          : `unknown API: ${t.text} (check the API panel)`,
      });
    }
    if (dialect === 'contract' && (t.text === 'obj.get' || t.text === 'obj.set')) {
      const first = toks[k + 2];
      if (first && first.kind !== 'num') {
        diags.push({ line: t.line, col: t.col, severity: 'error', message: `${t.text}: objId must be a numeric literal (compiler rule — expressions are not lowered)` });
      }
    }
  }

  // ── world script must return a hook table ──────────────────────────────────
  if (dialect === 'worldscript') {
    const hasReturn = toks.some((t, k) => t.kind === 'kw' && t.text === 'return' && toks[k + 1]?.text === '{');
    if (!hasReturn) {
      diags.push({ line: 1, col: 1, severity: 'warning', message: 'world scripts should end with `return { on_init = …, on_tick = …, on_event = … }` — without it no hooks run' });
    }
  }

  return diags;
}

/** Map a server compiler error ("line 12: unknown builtin …") to a diagnostic. */
export function diagnosticFromServerError(message: string): LuaDiagnostic {
  const m = /line (\d+):?\s*(.*)/.exec(message);
  return m
    ? { line: Math.max(1, parseInt(m[1], 10)), col: 1, message: m[2] || message, severity: 'error' }
    : { line: 1, col: 1, message, severity: 'error' };
}
